"use client";

import { useEffect } from "react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";

/**
 * CSS custom properties that get overridden when a client brand kit is active.
 * When no client brand kit exists (or clientId is null/undefined),
 * these overrides are removed and the Risdall defaults from globals.css apply.
 */
const BRAND_CSS_VARS = [
  "--sidebar",
  "--sidebar-accent",
  "--sidebar-foreground",
  "--primary",
] as const;

/**
 * Attempt to lighten a hex color for use as the sidebar-accent hover state.
 * Adds ~15% lightness by blending toward white. Returns an oklch-ish
 * approximation when possible, or falls back to a simple hex lighten.
 */
function lightenHex(hex: string): string {
  const cleaned = hex.replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(cleaned)) return hex;

  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);

  // Lighten by mixing 30% toward white
  const mix = 0.3;
  const lr = Math.round(r + (255 - r) * mix);
  const lg = Math.round(g + (255 - g) * mix);
  const lb = Math.round(b + (255 - b) * mix);

  return `#${lr.toString(16).padStart(2, "0")}${lg.toString(16).padStart(2, "0")}${lb.toString(16).padStart(2, "0")}`;
}

/**
 * Hook that applies client brand colors as CSS custom properties on <html>.
 *
 * - Takes a clientId (or undefined for agency/default context)
 * - Queries the client's brand kit from Convex
 * - Applies --sidebar, --sidebar-accent, --sidebar-foreground, --primary
 *   as inline CSS vars on document.documentElement
 * - Cleans up (removes overrides) on unmount or when client changes
 * - Falls back to doing nothing if no brand kit / no colors set
 */
export function useClientBrand(
  clientId: Id<"clients"> | undefined,
  setBrandLogoUrl?: (url: string | null) => void,
): {
  logoUrl: string | null;
} {
  const brandKit = useQuery(
    api.growthOs.getBrandKit,
    clientId ? { clientId } : "skip",
  );

  // Always pipe logoUrl into context whenever a brand kit exists,
  // regardless of whether colors changed or primaryColor is set.
  const logoUrl = brandKit?.logoUrl ?? null;
  useEffect(() => {
    setBrandLogoUrl?.(logoUrl);
  }, [logoUrl, setBrandLogoUrl]);

  useEffect(() => {
    const root = document.documentElement;

    // If no client selected, no brand kit found, or no primary color set:
    // ensure any previous overrides are cleaned up
    if (!clientId || !brandKit || !brandKit.primaryColor) {
      BRAND_CSS_VARS.forEach((prop) => root.style.removeProperty(prop));
      return;
    }

    const primary = brandKit.primaryColor;
    const accent = lightenHex(primary);

    root.style.setProperty("--sidebar", primary);
    root.style.setProperty("--sidebar-accent", accent);
    root.style.setProperty("--sidebar-foreground", "#ffffff");
    root.style.setProperty("--primary", primary);

    // Cleanup on unmount or clientId/brandKit change
    return () => {
      BRAND_CSS_VARS.forEach((prop) => root.style.removeProperty(prop));
    };
  }, [clientId, brandKit]);

  return {
    logoUrl,
  };
}
